import React, { useState } from 'react';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EastIcon from '@mui/icons-material/East';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Link from '@mui/material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const TerzaghiResult = ({ result }) => {
  const { cohesion, phi, df, fndWidth, gamma, fndType, gwt, FsSt, FsSs, FSt, FSs } = result.inputs;
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const {
    nq,
    nc,
    ngamma,
    sc,
    sgamma,
    effectiveStress,
    dw,
    h,
    gammaEffective,
    gammaEffectivePrime,
    appliedCase,
    qUlt,
    qAllSt,
    qAllSs,
    isQAllStEnough,
    isQAllSsEnough,
    canUserSeeCalculationSteps,
  } = result.outputs;

  const TerzaghiInfoBox = () => {
    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Terzaghi’s Bearing Capacity Equation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Terzaghi’s (1943) equation expresses the ultimate bearing capacity of shallow foundations as the combined effects of
            soil cohesion (<i>c</i>), overburden pressure (<i>q</i>), and unit weight (<i>γ</i>) beneath the footing. Each term is
            scaled by bearing capacity factors (
            <i>
              N<sub>c</sub>
            </i>
            ,{' '}
            <i>
              N<sub>q</sub>
            </i>
            ,
            <i>
              N<sub>γ</sub>
            </i>
            ) that depend on the soil friction angle (<i>φ</i>) and by shape factors (
            <i>
              s<sub>c</sub>
            </i>
            ,{' '}
            <i>
              s<sub>γ</sub>
            </i>
            ) reflecting footing geometry. It applies to general shear failure in homogeneous soils and remains the standard
            approach for preliminary design.
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              marginTop: '15px',
              marginBottom: '10px',
              lineHeight: 1.6,
              fontWeight: 600,
            }}
          >
            <math display="block">
              <msub>
                <mi>q</mi>
                <mi>ult</mi>
              </msub>
              <mo>=</mo>
              <mi>c</mi>
              <msub>
                <mi>N</mi>
                <mi>c</mi>
              </msub>
              <msub>
                <mi>s</mi>
                <mi>c</mi>
              </msub>
              <mo>+</mo>
              <mi>q</mi>
              <msub>
                <mi>N</mi>
                <mi>q</mi>
              </msub>
              <mo>+</mo>
              <mn>0.5</mn>
              <msub>
                <mi>γ</mi>
                <mi>e</mi>
              </msub>
              <mi>B</mi>
              <msub>
                <mi>N</mi>
                <mi>γ</mi>
              </msub>
              <msub>
                <mi>s</mi>
                <mi>γ</mi>
              </msub>
            </math>
          </Typography>
        </Alert>
      </Box>
    );
  };

  const BearingCapacityFactorsInfoBox = () => {
    const [showNcDetails, setShowNcDetails] = useState(false);
    const [showNqDetails, setShowNqDetails] = useState(false);
    const [showNgammaDetails, setShowNgammaDetails] = useState(false);

    const tableData = [
      { phi: 0, kpy: 10.8, ngamma: 0.0 },
      { phi: 5, kpy: 12.2, ngamma: 0.5 },
      { phi: 10, kpy: 14.7, ngamma: 1.2 },
      { phi: 15, kpy: 18.6, ngamma: 2.5 },
      { phi: 20, kpy: 25.0, ngamma: 5.0 },
      { phi: 25, kpy: 35.0, ngamma: 9.7 },
      { phi: 30, kpy: 52.0, ngamma: 19.7 },
      { phi: 34, kpy: '-', ngamma: 36 },
      { phi: 35, kpy: 82.0, ngamma: 42.4 },
      { phi: 40, kpy: 141.0, ngamma: 100.4 },
      { phi: 45, kpy: 298.0, ngamma: 297.5 },
      { phi: 48, kpy: '-', ngamma: 780.1 },
      { phi: 50, kpy: 800.0, ngamma: 1153.2 },
    ];

    // Determine highlighted rows
    const EPS = 1e-9;

    // exact match if φ equals any tabulated φ (e.g., 34°, 48° are allowed)
    const exactIdx = tableData.findIndex((r) => Math.abs(r.phi - phi) < EPS);

    // smallest index with φ_i ≥ φ
    let upperIdx = tableData.findIndex((r) => r.phi >= phi);

    // handle out-of-range
    if (upperIdx === -1) upperIdx = tableData.length - 1; // above max
    if (upperIdx === 0 && exactIdx === -1) {
      // below min and not exact: use first two rows for interpolation highlight
      // lower will be 0, upper 1
    }

    let lowerIdx;
    if (exactIdx !== -1) {
      lowerIdx = exactIdx; // not used, but defined
    } else {
      lowerIdx = Math.max(0, upperIdx - 1);
      // if φ above max, lowerIdx becomes last-1; if φ below min, lowerIdx=0, upperIdx=0 → adjust:
      if (upperIdx === 0) upperIdx = 1; // highlight rows 0 & 1
    }

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Bearing Capacity Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            The bearing capacity factors (
            <i>
              N<sub>c</sub>, N<sub>q</sub>, N<sub>γ</sub>
            </i>
            ) are dimensionless coefficients introduced by Terzaghi to account for the influence of soil friction angle (<i>φ</i>)
            on each term of the bearing capacity equation. They quantify the contribution of cohesion, surcharge, and soil weight
            respectively.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* surcharge factor - Nq */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNqDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNqDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  N<sub>q</sub>
                </b>{' '}
                (surcharge factor): {nq ? nq.toFixed(2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showNqDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    We can use the following formula to calculate the surcharge factor N<sub>q</sub>:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        {/* NUMERATOR */}
                        <mrow>
                          <msup>
                            <mi>e</mi>
                            <mi>
                              <mi>2(3&pi;/4 - &phi;/2)tan&phi;</mi>
                            </mi>
                          </msup>
                        </mrow>
                        {/* DENOMINATOR */}
                        <mrow>
                          <msup>
                            <mi>2cos</mi>
                            <mi>2</mi>
                          </msup>
                          <mi>(45 + &phi; /2)</mi>
                        </mrow>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Plugging in the variable φ into the equation, we will have:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        {/* NUMERATOR */}
                        <mrow>
                          <msup>
                            <mi>e</mi>
                            <mrow>
                              <mn>2</mn>
                              <mo>(</mo>
                              <mfrac>
                                <mrow>
                                  <mn>3</mn>
                                  <mi>π</mi>
                                </mrow>
                                <mn>4</mn>
                              </mfrac>
                              <mo>−</mo>
                              <mfrac>
                                <mn>{phi.toFixed(2)}</mn>
                                <mn>2</mn>
                              </mfrac>
                              <mo>)</mo>
                              <mo>tan</mo>
                              <mo>(</mo>
                              <mn>{phi.toFixed(2)}</mn>
                              <mo>)</mo>
                            </mrow>
                          </msup>
                        </mrow>
                        {/* DENOMINATOR (unchanged) */}
                        <mrow>
                          <msup>
                            <mrow>
                              <mn>2</mn>
                              <mo>cos</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                          <mo>(</mo>
                          <mn>45</mn>
                          <mo>+</mo>
                          <mfrac>
                            <mn>{phi.toFixed(2)}</mn>
                            <mn>2</mn>
                          </mfrac>
                          <mo>)</mo>
                        </mrow>
                      </mfrac>
                    </math>
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    And from here;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mn>{nq.toFixed(2)}</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* cohesion factor - Nc */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  N<sub>c</sub>
                </b>{' '}
                (cohesion factor): {nc ? nc.toFixed(2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showNcDetails} unmountOnExit>
                  {phi === 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        Since φ = 0<sup>o</sup>, the following formula should be used to calculate the cohesion factor N
                        <sub>c:</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mo> = </mo>
                          <mn>1.5</mn>
                          <mi>π</mi>
                          <mo> + </mo>
                          <mn>1</mn>
                          <mtext> [See Terzaghi (1943), p. 127.]</mtext>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        And from here;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mtext> = </mtext>
                          <mn>{nc.toFixed(2)}</mn>
                        </math>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                        We have already calculated that the surcharge factor, N<sub>q</sub>= {nq ? nq.toFixed(2) : '—'}. After
                        that, the following formula can be used to calculate the cohesion factor N<sub>c:</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math xmlns="http://www.w3.org/1998/Math/MathML" display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mo>=</mo>
                          <mrow>
                            <mo>(</mo>
                            <msub>
                              <mi>N</mi>
                              <mi>q</mi>
                            </msub>
                            <mo>−</mo>
                            <mn>1</mn>
                            <mo>)</mo>
                          </mrow>
                          <mo>cot</mo>
                          <mi>φ</mi>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        When we input also the input value φ, we will have this equation:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>

                          <mtext> = </mtext>
                          <mrow>
                            <mo>(</mo>
                            <mn>{nq.toFixed(2)}</mn>
                            <mo>−</mo>
                            <mn>1</mn>
                            <mo>)</mo>
                            <mo>×</mo>
                            <mo>cot(</mo>
                            <mn>{phi.toFixed(2)}</mn>
                            <mo>)</mo>
                          </mrow>
                        </math>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        And from here;
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <math display="block">
                          <msub>
                            <mi>N</mi>
                            <mi>c</mi>
                          </msub>
                          <mtext> = </mtext>
                          <mn>{nc.toFixed(2)}</mn>
                        </math>
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* unit weight factor - Ngamma */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowNgammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showNgammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  N<sub>&gamma;</sub>
                </b>{' '}
                (unit weight factor): {ngamma.toFixed(2)}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showNgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    The unit weight factor N<sub>γ</sub> represents the effect of soil self-weight on bearing capacity. Terzaghi
                    introduced the passive pressure coefficient{' '}
                    <b>
                      K<sub>py</sub>
                    </b>
                    , from which N<sub>γ</sub> can be derived. He did not describe in detail how K<sub>py</sub> was obtained but
                    provided three specific N<sub>γ</sub> values at φ = 0°, 34°, and 48°. Later researchers extended these points
                    into a complete φ–N<sub>γ</sub> curve and back-calculated K<sub>py</sub> values to form the commonly used
                    dataset.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {phi % 5 === 0 ? (
                      <>
                        For φ = {phi.toFixed(2)}°, since it is a multiple of 5, the exact tabulated value of N<sub>γ</sub> from
                        Terzaghi (1943) was used directly without interpolation.
                      </>
                    ) : (
                      <>
                        For φ = {phi.toFixed(2)}°, since it is not a multiple of 5, the unit weight factor N<sub>γ</sub> was
                        determined by linearly interpolating between the two closest tabulated φ values of Terzaghi (1943).
                      </>
                    )}
                  </Typography>

                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      marginBottom: '1rem',
                      fontSize: '0.875rem',
                      textAlign: 'center',
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(46,125,50,0.04)' }}>
                        <th style={{ border: '1px solid #ccc', padding: '6px' }}>φ (°)</th>
                        <th style={{ border: '1px solid #ccc', padding: '6px' }}>
                          K<sub>py</sub>
                        </th>
                        <th style={{ border: '1px solid #ccc', padding: '6px' }}>
                          N<sub>γ</sub>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, i) => {
                        let bg = '';

                        if (exactIdx !== -1) {
                          // highlight only the exact row (green)
                          if (i === exactIdx) bg = 'rgba(76,175,80,0.15)';
                        } else {
                          // highlight the two neighbor rows used for interpolation (yellow)
                          if (i === lowerIdx || i === upperIdx) bg = 'rgba(255,235,59,0.15)';
                        }

                        return (
                          <tr key={row.phi} style={{ backgroundColor: bg }}>
                            <td style={{ border: '1px solid #ccc', padding: '6px' }}>{row.phi}</td>
                            <td style={{ border: '1px solid #ccc', padding: '6px' }}>{row.kpy}</td>
                            <td style={{ border: '1px solid #ccc', padding: '6px' }}>{row.ngamma}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Reference Values for K<sub>py</sub> and N<sub>γ</sub> (Terzaghi, 1943)
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const ShapeFactorsInfoBox = () => {
    const [showScDetails, setShowScDetails] = useState(false);
    const [showSgammaDetails, setShowSgammaDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Shape Factors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            The shape factors (sc, sγ) modify the bearing capacity equation to account for foundation geometry. These factors
            adjust the contributions of cohesion, and unit weight terms for different footing shapes such as strip, square, or
            circular.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* cohesion shape factor - sq */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowScDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showScDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  s<sub>c</sub>
                </b>{' '}
                (cohesion shape factor): {sc ? sc.toFixed(2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showScDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can use the following expressions to select the cohesion shape factor (s<sub>c</sub>) according to Terzaghi
                    (1943):
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    • For strip footings: s<sub>c</sub> = 1.0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    • For circular footings: s<sub>c</sub> = 1.3
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    • For square footings: s<sub>c</sub> = 1.3
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Since the selected foundation type is <b>{fndType === 1 ? 'Strip' : fndType === 2 ? 'Circular' : 'Square'}</b>
                    ;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>s</mi>
                        <mi>c</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mn>{sc.toFixed(2)}</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* unit weight shape factor - sgamma */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSgammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSgammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  s<sub>&gamma;</sub>
                </b>{' '}
                (unit weight shape factor): {sgamma ? sgamma.toFixed(2) : '—'}
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can use the following expressions to select the unit weight shape factor (s<sub>&gamma;</sub>) according to
                    Terzaghi (1943):
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    • For strip footings: s<sub>&gamma;</sub> = 1.0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    • For circular footings: s<sub>&gamma;</sub> = 0.6
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    • For square footings: s<sub>&gamma;</sub> = 0.8
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Since the selected foundation type is <b>{fndType === 1 ? 'Strip' : fndType === 2 ? 'Circular' : 'Square'}</b>
                    ;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>s</mi>
                        <mi>&gamma;</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mn>{sgamma.toFixed(2)}</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const WaterTableGammaEInfoBox = () => {
    const [open, setOpen] = useState(false);
    const gammaW = 9.806; // water unit weight, used as "gravity" in backend
    const gammaPrime = gamma - gammaW;

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert
          icon={isSmallScreen ? false : <InfoOutlinedIcon />}
          severity="info"
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
            Effect of Water Table — γ<sub>e</sub> Term
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            To account for the water table on the unit-weight term of Terzaghi’s equation, an average effective unit weight γ
            <sub>e</sub> is used over the failure wedge depth H. Your solver follows the Bowles-style wedge interpolation when the
            water table intersects the wedge.
          </Typography>

          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              color: 'inherit',
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton size="small" color="inherit" onClick={() => setOpen((v) => !v)}>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.15s' }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography variant="body2" color="text.secondary">
              <b>
                γ<sub>e</sub>
              </b>{' '}
              (av. effective unit weight):&nbsp;{gammaEffective.toFixed(2)} kN/m³ , Case {appliedCase.caseId}:{appliedCase.note}
            </Typography>
            {/* Collapsible details */}
            {canUserSeeCalculationSteps && (
              <Collapse in={open} unmountOnExit>
                <Box sx={{ pl: 0.5, pr: 0.5 }} textAlign={'center'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5, mt: 1, lineHeight: 1.6, fontWeight: 600 }}
                  >
                    Piecewise rule:
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    Case 1 - If GWT ≤ D<sub>f</sub>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    γ<sub>e</sub> = γ′ = γ − γ<sub>w</sub>
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    Case 2 - If D<sub>f</sub> &lt; GWT ≤ H{' '}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <math>
                      <msub>
                        <mi>γ</mi>
                        <mi>e</mi>
                      </msub>
                      <mo>=</mo>
                      <mrow>
                        <mi>γ</mi>
                        <mo>&#x2062;</mo>
                        <mo>[</mo>
                        <mfrac>
                          <mrow>
                            <mo>(</mo>
                            <mn>2</mn>
                            <mi>H</mi>
                            <mo>−</mo>
                            <msub>
                              <mi>d</mi>
                              <mi>w</mi>
                            </msub>
                            <mo>)</mo>
                            <msub>
                              <mi>d</mi>
                              <mi>w</mi>
                            </msub>
                          </mrow>
                          <msup>
                            <mi>H</mi>
                            <mn>2</mn>
                          </msup>
                        </mfrac>
                        <mo>]</mo>
                      </mrow>
                      <mo>+</mo>
                      <mrow>
                        <mi>γ</mi>
                        <mo>′</mo>
                        <mo>&#x2062;</mo>
                        <mo>[</mo>
                        <mfrac>
                          <msup>
                            <mrow>
                              <mo>(</mo>
                              <mi>H</mi>
                              <mo>−</mo>
                              <msub>
                                <mi>d</mi>
                                <mi>w</mi>
                              </msub>
                              <mo>)</mo>
                            </mrow>
                            <mn>2</mn>
                          </msup>
                          <msup>
                            <mi>H</mi>
                            <mn>2</mn>
                          </msup>
                        </mfrac>
                        <mo>]</mo>
                      </mrow>
                    </math>
                  </Typography>

                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    Case 3 - If GWT &gt; H
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    γ<sub>e</sub> = γ
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                    Where;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <math>
                      <mi>H</mi>
                      <mo>=</mo>
                      <msub>
                        <mi>D</mi>
                        <mi>f</mi>
                      </msub>
                      <mo>+</mo>
                      <mn>0.5</mn>
                      <mi>B</mi>
                      <mo>tan</mo>
                      <mo>(</mo>
                      <mn>45</mn>
                      <mo>°</mo>
                      <mo>+</mo>
                      <mi>φ</mi>
                      <mo>/</mo>
                      <mn>2</mn>
                      <mo>)</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <math display="inline">
                      <msub>
                        <mi>d</mi>
                        <mi>w</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>GWT</mi>
                      <mo>−</mo>
                      <msub>
                        <mi>D</mi>
                        <mi>f</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <math>
                      <mi>γ</mi>
                      <mo>′</mo>
                      <mo>=</mo>
                      <mi>γ</mi>
                      <mo>−</mo>
                      <msub>
                        <mi>γ</mi>
                        <mi>w</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>
                    Input Parameters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    D<sub>f</sub> = {df} m,&nbsp; B = {fndWidth} m,&nbsp; φ = {phi}°,&nbsp; GWT = {gwt} m ,&nbsp;γ = {gamma}
                    kN/m³,&nbsp;γ<sub>w</sub> = 9.806 kN/m³
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mt: 1 }}>
                    Calculated Parameters
                  </Typography>
                  {appliedCase.caseId === 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        It is known from the inputs that GWT = {gwt}m &le; D<sub>f</sub> = {df}m. So, use <b>Case 1</b>{' '}
                        formulation to calculate γ<sub>e</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        γ<sub>e</sub> = γ′ = γ − γ<sub>w</sub> = {gamma.toFixed(2)} - {gammaW.toFixed(2)} ={' '}
                        {gammaPrime.toFixed(2)} kN/m³
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        γ<sub>e</sub> = {gammaPrime.toFixed(2)} kN/m³
                      </Typography>
                    </>
                  )}
                  {appliedCase.caseId === 2 && (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        It is known from the inputs that GWT = {gwt}m &gt; D<sub>f</sub> = {df}m. So, we should also check the
                        failure wedge depth (H) to find if the case is 2 or 3.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = D<sub>f</sub> + 0.5Btan(45 + &phi;/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {df} + (0.5)({fndWidth})tan(45 + {phi}/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {h.toFixed(2)}m
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        {' '}
                        Since GWT = {gwt}m &le; H = {h.toFixed(2)}m, use <b>Case 2</b> formulation to calculate γ<sub>e</sub>
                      </Typography>

                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2} className="math-scale">
                        <math display="inline">
                          <msub>
                            <mi>γ</mi>
                            <mi>e</mi>
                          </msub>
                          <mo>=</mo>
                          <mn>{gamma.toFixed(2)}</mn>
                          <mo>[</mo>
                          <mfrac>
                            <mrow>
                              <mo>(</mo>
                              <mn>2</mn>
                              <mo>×</mo>
                              <mn>{h.toFixed(2)}</mn>
                              <mo>−</mo>
                              <mn>{dw.toFixed(2)}</mn>
                              <mo>)</mo>
                              <mn>{dw.toFixed(2)}</mn>
                            </mrow>
                            <msup>
                              <mn>{h.toFixed(2)}</mn>
                              <mn>2</mn>
                            </msup>
                          </mfrac>
                          <mo>]</mo>
                          <mo>+</mo>
                          <mn>{gammaEffectivePrime.toFixed(2)}</mn>
                          <mo>[</mo>
                          <mfrac>
                            <msup>
                              <mrow>
                                <mo>(</mo>
                                <mn>{h.toFixed(2)}</mn>
                                <mo>−</mo>
                                <mn>{dw.toFixed(2)}</mn>
                                <mo>)</mo>
                              </mrow>
                              <mn>2</mn>
                            </msup>
                            <msup>
                              <mn>{h.toFixed(2)}</mn>
                              <mn>2</mn>
                            </msup>
                          </mfrac>
                          <mo>]</mo>
                        </math>
                      </Typography>

                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        &gamma;<sub>e</sub> = {gammaEffective.toFixed(2)} kN/m³
                      </Typography>
                    </>
                  )}
                  {appliedCase.caseId === 3 && (
                    <>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        It is known from the inputs that GWT = {gwt}m &gt; D<sub>f</sub> = {df}m. So, we should also check the
                        failure wedge depth (H) to find if the case is 2 or 3.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = D<sub>f</sub> + 0.5Btan(45 + &phi;/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {df} + (0.5)({fndWidth})tan(45 + {phi}/2)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        H = {h.toFixed(2)}m
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        {' '}
                        Since GWT = {gwt}m &gt; H = {h.toFixed(2)}m, use <b>Case 3</b> formulation to calculate γ<sub>e</sub>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign={'center'} mb={2}>
                        &gamma;<sub>e</sub> = &gamma; = {gammaEffective.toFixed(2)} kN/m³
                      </Typography>
                    </>
                  )}
                </Box>
              </Collapse>
            )}
          </Alert>
        </Alert>
      </Box>
    );
  };

  const BearingCapacityInfoBox = () => {
    const [showUltimateBearingCapacityDetails, setShowUltimateBearingCapacityDetails] = useState(false);
    const [showStaticAllowableBearingCapacityDetails, setShowStaticAllowableBearingCapacityDetails] = useState(false);
    const [showSeismicAllowableBearingCapacityDetails, setShowSeismicAllowableBearingCapacityDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Bearing Capacity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            The bearing capacity represents the maximum pressure that the foundation soil can sustain without failure. It is first
            calculated as the ultimate bearing capacity (q<sub>ult</sub>), which combines the effects of cohesion, surcharge, and
            soil unit weight, including shape factors that adjust for foundation geometry.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            The allowable bearing capacity (q<sub>all</sub>) is then obtained by dividing q<sub>ult</sub> by an appropriate factor
            of safety (F.S.), typically between 2.5 and 3 for static case and between 1.00 to 1.10 for seismic case. This
            represents the safe working load that the soil can carry without excessive settlement or failure.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Ultimate Bearing Capacity */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowUltimateBearingCapacityDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showUltimateBearingCapacityDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  q<sub>ult</sub>
                </b>{' '}
                (ultimate bearing capacity): {qUlt ? qUlt.toFixed(0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showUltimateBearingCapacityDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now substitute all previously calculated factors and input parameters into Terzaghi’s general bearing
                    capacity equation to determine the ultimate bearing capacity (q<sub>ult</sub>).
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>c</mi>
                      <msub>
                        <mi>N</mi>
                        <mi>c</mi>
                      </msub>
                      <msub>
                        <mi>s</mi>
                        <mi>c</mi>
                      </msub>
                      <mo>+</mo>
                      <mi>q</mi>
                      <msub>
                        <mi>N</mi>
                        <mi>q</mi>
                      </msub>
                      <mo>+</mo>
                      <mn>0.5</mn>
                      <msub>
                        <mi>γ</mi>
                        <mi>e</mi>
                      </msub>
                      <mi>B</mi>
                      <msub>
                        <mi>N</mi>
                        <mi>γ</mi>
                      </msub>
                      <msub>
                        <mi>s</mi>
                        <mi>γ</mi>
                      </msub>
                    </math>
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={1}
                    mt={2}
                    sx={{
                      textAlign: 'center',

                      fontStyle: 'italic',
                      fontSize: '14px',
                      letterSpacing: '0.4px',
                    }}
                  >
                    q<sub>ult</sub> ={cohesion != null ? cohesion.toFixed(2) : '—'}×{nc != null ? nc.toFixed(2) : '—'}×
                    {sc != null ? sc.toFixed(2) : '—'} + {effectiveStress != null ? effectiveStress.toFixed(2) : '—'}×
                    {nq != null ? nq.toFixed(2) : '—'} + 0.50×{gammaEffective != null ? gammaEffective.toFixed(2) : '—'} ×{' '}
                    {fndWidth != null ? fndWidth.toFixed(2) : '—'}×{ngamma != null ? ngamma.toFixed(2) : '—'}×
                    {sgamma != null ? sgamma.toFixed(2) : '—'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>ult</mi>
                      </msub>
                      <mtext> = </mtext>
                      <mn>{qUlt.toFixed(0)} kPa</mn>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Allowabile Capacity - Static */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowStaticAllowableBearingCapacityDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showStaticAllowableBearingCapacityDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  q<sub>all-st</sub>
                </b>{' '}
                (allowable bearing capacity - static case): {qAllSt ? qAllSt.toFixed(0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showStaticAllowableBearingCapacityDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={2} mt={2}>
                    We can now determine the allowable bearing capacity for the static case (q<sub>all-st</sub>)by applying the
                    factor of safety you provided (FS<sub>St</sub> = {FsSt.toFixed(2)}) to the previously computed ultimate
                    bearing capacity. This ensures that the working load on the foundation remains within safe limits under
                    long-term static conditions.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>st</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>q</mi>
                          <mi>ult</mi>
                        </msub>
                        <msub>
                          <mi>FS</mi>
                          <mrow>
                            <mi>st</mi>
                          </mrow>
                        </msub>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>st</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUlt != null ? qUlt.toFixed(0) : '—'}</mn>
                        <mn>{FsSt != null ? FsSt.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    From this, the allowable bearing capacity for the static case is:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>st</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mn>{qAllSt != null ? qAllSt.toFixed(0) : '—'}</mn>
                      <mo> </mo>
                      <mi>kPa</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* Allowabile Capacity - Seismic */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSeismicAllowableBearingCapacityDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSeismicAllowableBearingCapacityDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  q<sub>all-ss</sub>
                </b>{' '}
                (allowable bearing capacity - seismic case): {qAllSs ? qAllSs.toFixed(0) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSeismicAllowableBearingCapacityDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={2} mt={2}>
                    We can now determine the allowable bearing capacity for the seismic case (q<sub>all-ss</sub>) by applying the
                    seismic factor of safety you provided (Fs<sub>ss</sub> = {FsSs.toFixed(2)}) to the previously computed
                    ultimate bearing capacity. This represents the reduced safe bearing pressure under earthquake loading,
                    ensuring the foundation remains stable during seismic events.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>ss</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <msub>
                          <mi>q</mi>
                          <mi>ult</mi>
                        </msub>
                        <msub>
                          <mi>FS</mi>
                          <mrow>
                            <mi>ss</mi>
                          </mrow>
                        </msub>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>ss</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>{qUlt != null ? qUlt.toFixed(0) : '—'}</mn>
                        <mn>{FsSs != null ? FsSs.toFixed(2) : '—'}</mn>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    From this, the allowable bearing capacity for the seismic case is:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mrow>
                          <mi>all</mi>
                          <mo>-</mo>
                          <mi>ss</mi>
                        </mrow>
                      </msub>
                      <mo>=</mo>
                      <mn>{qAllSs != null ? qAllSs.toFixed(0) : '—'}</mn>
                      <mo> </mo>
                      <mi>kPa</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const BearingCapacityVsLoadsInfoBox = () => {
    const [showBearingCapacityVsLoadsDetailsStatic, setShowBearingCapacityVsLoadsDetailsStatic] = useState(false);
    const [showBearingCapacityVsLoadsDetailsSeismic, setShowBearingCapacityVsLoadsDetailsSeismic] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            Allowable Bearing Capacities Vs Loads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
            Finally, the computed allowable bearing capacity values will be compared with the design loads you provided. This
            comparison ensures that the applied foundation pressures remain below the allowable limits, confirming that the soil
            can safely support the imposed loads without exceeding its bearing capacity in static and seismic cases.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Static Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsStatic((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsStatic ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Static Case:</b>
                {isQAllStEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsStatic} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the static allowable bearing capacity (q<sub>all-st</sub>) with the static foundation
                    pressure you provided to assess whether the soil can safely support the applied load under static conditions.
                  </Typography>

                  {isQAllStEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-st</sub> = {qAllSt.toFixed(0)} kPa &gt; FSt = {FSt.toFixed(0)} kPa
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity exceeds the applied static foundation pressure.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-st</sub> = {qAllSt.toFixed(0)} kPa &lt; FSt = {FSt.toFixed(0)} kPa
                      </Typography>

                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity is less than the applied static foundation pressure.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* Seismic Case */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowBearingCapacityVsLoadsDetailsSeismic((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showBearingCapacityVsLoadsDetailsSeismic ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Seismic Case:</b>
                {isQAllSsEnough ? (
                  <>
                    <VerifiedIcon sx={{ color: 'success.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      OK!
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 22 }} />{' '}
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Not OK!
                    </Typography>
                  </>
                )}
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showBearingCapacityVsLoadsDetailsSeismic} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    We can now compare the seismic allowable bearing capacity (q<sub>all-ss</sub>) with the seismic foundation
                    pressure you provided to assess whether the soil can safely support the applied load under seismic conditions.
                  </Typography>

                  {isQAllSsEnough ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-ss</sub> = {qAllSs.toFixed(0)} kPa &gt; FSs = {FSs.toFixed(0)} kPa
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        The allowable bearing capacity exceeds the applied seismic foundation pressure.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={1} textAlign={'center'}>
                        q<sub>all-ss</sub> = {qAllSs.toFixed(0)} kPa &lt; FSs = {FSs.toFixed(0)} kPa
                      </Typography>

                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        The allowable bearing capacity is less than the applied seismic foundation pressure.
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };
  return (
    <section className="output terzaghiOutput">
      <h2>Output </h2>
      <TerzaghiInfoBox />
      <BearingCapacityFactorsInfoBox />
      <ShapeFactorsInfoBox />
      <WaterTableGammaEInfoBox />
      <BearingCapacityInfoBox />
      <BearingCapacityVsLoadsInfoBox />
    </section>
  );
};

export default TerzaghiResult;
